import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target,
  Calendar,
  TrendingUp,
  CheckCircle,
  Lightbulb,
  Sparkles,
  Printer,
  Share2,
} from 'lucide-react';
import { ReportSection } from '@/components/ReportSection';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { api, ProductivityReport } from '@/lib/api';

export default function ReportViewPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ProductivityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }
    loadReport();
  }, [userId, navigate]);

  const loadReport = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const reportData = await api.getReport(userId);
      setReport(reportData);
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Report not found or no longer available.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const url = window.location.href;

    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCreateAccount = () => {
    // Redirect to app with soft user ID for conversion
    window.location.href = `https://app.mybestday.ai?convertFrom=${userId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Loading productivity plan..." size="large" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="card-simple max-w-md text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Report Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'This report may have been deleted or is no longer available.'}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Create Your Own Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="section">
        <div className="container-narrow">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="heading">Productivity Plan</h1>
            <p className="subheading">
              A personalized productivity plan powered by AI
            </p>
          </motion.div>

          {/* Action Buttons */}
          <div className="card mb-8 no-print">
            <div className="flex flex-wrap gap-3">
              <button onClick={handleShare} className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none">
                <Share2 size={18} />
                {copySuccess ? 'Link Copied!' : 'Share'}
              </button>
              <button onClick={handlePrint} className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none">
                <Printer size={18} />
                Print
              </button>
              <button onClick={() => navigate('/')} className="btn-primary flex items-center gap-2 flex-1 sm:flex-auto">
                <Sparkles size={18} />
                Create Your Own Plan
              </button>
            </div>
          </div>

          {/* Priority Goals */}
          <ReportSection title="Goals (Prioritized)" icon={Target}>
            <div className="space-y-4">
              {report.goals
                .sort((a, b) => b.priority - a.priority)
                .map((goal, index) => (
                  <div key={index} className="card-simple">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white">
                        {index + 1}
                      </div>
                      <div className="flex-grow">
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">
                          {goal.description}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">{goal.reasoning}</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-gray-500">
                            <strong>Priority:</strong> {goal.priority}/100
                          </span>
                          <span className="text-gray-500">
                            <strong>Est. Time:</strong> {goal.estimatedTimeToComplete}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </ReportSection>

          {/* Timeline */}
          <ReportSection title="Timeline" icon={Calendar}>
            <div className="space-y-4">
              {report.recommendations.timeline.map((phase, index) => (
                <div key={index} className="card-simple border-l-4 border-primary-600">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{phase.phase}</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Duration:</strong> {phase.duration}
                  </p>
                  <div className="mb-3">
                    <strong className="text-sm text-gray-700">Goals:</strong>
                    <ul className="list-disc list-inside text-sm text-gray-600 ml-2 mt-1">
                      {phase.goals.map((goal, i) => (
                        <li key={i}>{goal}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">Milestones:</strong>
                    <ul className="list-disc list-inside text-sm text-gray-600 ml-2 mt-1">
                      {phase.milestones.map((milestone, i) => (
                        <li key={i}>{milestone}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </ReportSection>

          {/* Habits */}
          <ReportSection title="Recommended Habits" icon={TrendingUp}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Daily Habits</h3>
                <ul className="space-y-2">
                  {report.recommendations.dailyHabits.map((habit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="text-success-600 flex-shrink-0 mt-0.5" size={18} />
                      <span>{habit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Weekly Habits</h3>
                <ul className="space-y-2">
                  {report.recommendations.weeklyHabits.map((habit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="text-success-600 flex-shrink-0 mt-0.5" size={18} />
                      <span>{habit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ReportSection>

          {/* Next Actions */}
          <ReportSection title="Immediate Next Actions" icon={Target}>
            <div className="space-y-4">
              {Object.entries(report.recommendations.nextActions).map(([goal, actions], index) => (
                <div key={index} className="card-simple bg-blue-50 border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-3">{goal}</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    {actions.map((action, i) => (
                      <li key={i}>{action}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </ReportSection>

          {/* Strategies */}
          <ReportSection title="Success Strategies" icon={Lightbulb}>
            <ul className="space-y-3">
              {report.recommendations.strategies.map((strategy, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 p-4 bg-accent-50 border border-accent-200 rounded-lg"
                >
                  <Lightbulb className="text-accent-600 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-800">{strategy}</span>
                </li>
              ))}
            </ul>
          </ReportSection>

          {/* Custom Advice */}
          <ReportSection title="Personalized Advice" icon={Sparkles}>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {report.customAdvice}
              </p>
            </div>
          </ReportSection>

          {/* CTA to Create Own Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card bg-gradient-to-br from-primary-600 to-blue-600 text-white no-print mt-12"
          >
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Want Your Own Personalized Plan?
              </h2>
              <p className="text-lg text-primary-100 mb-6">
                Get AI-powered productivity tips tailored to your unique goals in just 2 minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => navigate('/')} className="btn-secondary btn-large">
                  <Target size={20} />
                  <span>Create Free Plan</span>
                </button>
                <button onClick={handleCreateAccount} className="btn-primary btn-large">
                  <Sparkles size={20} />
                  <span>Try My Best Day</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
